import { Button, Grid, Text } from '@mantine/core';
import { ErrorBoundary } from '@sentry/react';
import { IconArrowRight, IconZoomMoney } from '@tabler/icons-react';
import { PageTitle } from 'components/PageTitle';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLoadAgedBalanceCompact } from 'shared/hooks/use-load-aged-balance';
import { useLoadAgedBalanceOverTime } from 'shared/hooks/use-load-aged-balance-over-time';

import { UpdatedAt } from '../components';
import { AnalyticsCard } from '../components/AnalyticsCard';
import { CeiCard } from '../components/cards/CeiCard';
import { CollectionCohortsCard } from '../components/cards/CollectionCohortsCard';
import { DsoCard } from '../components/cards/DsoCard';
import { MoreChartsCard } from '../components/cards/MoreChartsCard';
import { PaidByReminderCard } from '../components/cards/PaidByReminderCard';
import { PaidByReminderWorkflowsCard } from '../components/cards/PaidByReminderWorkflowsCard';
import { RemindersAndCollectionsCard } from '../components/cards/RemindersAndCollectionsCard';
import { AgedBalanceCompactChart, AgedBalanceOverTimeChart } from '../components/charts';
import { TickersBanner } from '../components/TickersBanner';

const DEFAULT_PAID_BY_REMINDER_PERIOD_MONTHS = 6;
const COLLECTION_COHORTS_PERIOD_MONTHS = 12;

export const Overview = () => {
  const { t } = useTranslation();
  const [paidByReminderPeriodMonths, setPaidByReminderPeriodMonths] = useState(
    DEFAULT_PAID_BY_REMINDER_PERIOD_MONTHS,
  );

  const { agedBalanceOverTime } = useLoadAgedBalanceOverTime({ viewId: null });
  const { agedBalanceCompact } = useLoadAgedBalanceCompact();

  const shouldFallBackToCompactChart =
    agedBalanceOverTime != null && Object.keys(agedBalanceOverTime.data).length < 2;

  const seeBreakdownButton = (
    <Button
      component={Link}
      to="/analytics/aged-balance"
      variant="default"
      size="compact-sm"
      leftSection={<IconZoomMoney size={20} stroke={1.5} />}
      rightSection={<IconArrowRight size={20} stroke={1.5} />}
    >
      {t(i18nKeys.ANALYTICS.AGED_BALANCE.SEE_BREAKDOWN)}
    </Button>
  );

  return (
    <>
      <PageTitle>
        {t(i18nKeys.ANALYTICS.TITLE)}
        <PageTitle.Actions>
          <UpdatedAt />
        </PageTitle.Actions>
      </PageTitle>
      <TickersBanner />
      <Grid style={{ overflow: 'visible' }}>
        <Grid.Col span={12}>
          {shouldFallBackToCompactChart ? (
            <AnalyticsCard
              title={t(i18nKeys.ANALYTICS.AGED_BALANCE.TITLE)}
              actions={seeBreakdownButton}
            >
              <AgedBalanceCompactChart agedBalanceCompact={agedBalanceCompact} />
            </AnalyticsCard>
          ) : (
            <AgedBalanceOverTimeChart
              selectedViewId={null}
              titleActions={seeBreakdownButton}
              areDateRangeAndExportControlsVisible={false}
            />
          )}
        </Grid.Col>
        <Grid.Col span={6}>
          <ErrorBoundary fallback={<Text>Error while loading CEI</Text>}>
            <CeiCard />
          </ErrorBoundary>
        </Grid.Col>
        <Grid.Col span={6}>
          <DsoCard />
        </Grid.Col>
        <Grid.Col span={6}>
          <ErrorBoundary fallback={<Text>Error while loading paid by reminder</Text>}>
            <PaidByReminderCard
              periodMonths={paidByReminderPeriodMonths}
              onPeriodMonthsChange={setPaidByReminderPeriodMonths}
            />
          </ErrorBoundary>
        </Grid.Col>
        <Grid.Col span={6}>
          <ErrorBoundary fallback={<Text>Error while loading paid by reminder workflows</Text>}>
            <PaidByReminderWorkflowsCard periodMonths={paidByReminderPeriodMonths} />
          </ErrorBoundary>
        </Grid.Col>
        <Grid.Col span={12}>
          <RemindersAndCollectionsCard />
        </Grid.Col>
        <Grid.Col span={7}>
          <ErrorBoundary fallback={<Text>Error while loading collection cohorts</Text>}>
            <CollectionCohortsCard periodMonths={COLLECTION_COHORTS_PERIOD_MONTHS} />
          </ErrorBoundary>
        </Grid.Col>
        <Grid.Col span={5}>
          <MoreChartsCard />
        </Grid.Col>
      </Grid>
    </>
  );
};
